// Single source of truth for the Supabase Bearer token used by /api/* requests.
//
// History: api.ts and authFetch.ts each had their own module-level token cache,
// each calling supabase.auth.getSession() independently. When a page fired both
// helpers in parallel (e.g. customers/page.tsx Promise.all([getCustomers(),
// authFetch(...)])), both caches missed simultaneously, both raced for
// Supabase's Web Lock, and one side could hang indefinitely. authFetch.ts had
// no timeout on its getSession call, so the parent Promise.all never resolved
// and the page sat on `loading: true` until manual refresh.
//
// This module unifies both caches. Every Bearer-token-bearing request goes
// through getToken() here, so there is exactly one in-flight getSession() per
// 30s window across the whole app.
//
// Both api.ts and authFetch.ts re-export their original clear function names
// pointing at clearTokenCache() so existing callers (auth-context signOut)
// keep working.

const TOKEN_TTL_MS = 30_000;
const SESSION_TIMEOUT_MS = 3_000;

let _cachedToken: string | null = null;
let _cachedAt = 0;
let _pendingToken: Promise<string | null> | null = null;

export function clearTokenCache() {
  _cachedToken = null;
  _cachedAt = 0;
  _pendingToken = null;
}

export function getCachedToken(): string | null {
  return _cachedToken;
}

// Force-refresh path: invalidate the cache and ask Supabase for a fresh
// access token before the next call returns.
export async function forceRefreshToken(): Promise<string | null> {
  return getToken({ forceRefresh: true });
}

interface GetTokenOpts {
  forceRefresh?: boolean;
  timeoutMs?: number;
}

export async function getToken(opts: GetTokenOpts = {}): Promise<string | null> {
  const { forceRefresh = false, timeoutMs = SESSION_TIMEOUT_MS } = opts;
  if (typeof window === 'undefined') return null;

  if (!forceRefresh && _cachedToken && Date.now() - _cachedAt < TOKEN_TTL_MS) {
    return _cachedToken;
  }

  // Single-flight: coalesce concurrent callers onto one in-flight promise so
  // we never make two parallel getSession() calls. Force-refresh starts a new
  // call (after the prior one settles) since callers want a guaranteed-fresh
  // token after a 401.
  if (!forceRefresh && _pendingToken) return _pendingToken;

  _pendingToken = (async () => {
    try {
      const { supabase } = await import('./supabase');
      if (forceRefresh) {
        try { await supabase.auth.refreshSession(); } catch { /* fall through to getSession */ }
      }

      // Race getSession against a hard timeout. If Supabase's Web Lock is
      // contended (or its auth backend is slow) we'd rather return the prior
      // cached token (or null) than hang the calling page forever. The
      // request that goes out without a fresh token will 401, and the
      // caller's 401 path can then force-refresh.
      const sessionPromise = supabase.auth.getSession()
        .then(({ data: { session } }) => session?.access_token ?? null);
      const timeout = new Promise<string | null>((resolve) => {
        setTimeout(() => resolve(_cachedToken), timeoutMs);
      });
      const token = await Promise.race([sessionPromise, timeout]);

      _cachedToken = token;
      _cachedAt = Date.now();
      return token;
    } catch {
      return _cachedToken;
    } finally {
      _pendingToken = null;
    }
  })();

  return _pendingToken;
}
